// skillsCacheContext.tsx
import React, { 
  useState, 
  useCallback, 
  useContext, 
  createContext, 
  useEffect,
  useRef 
} from 'react';
import { Skill, TreeLink, SkillsTreeResponse } from '../types/microsoftGraph';
import { SkillHeader } from '../types/microsoftGraph';
import { api, API_ENDPOINTS } from '../config/api';

interface SkillsCacheContextType {
  skills: Skill[];
  links: TreeLink[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  hasSkills: boolean;
  fetchSkills: (forceRefresh?: boolean) => Promise<void>;
  clearCache: () => void;
  getSkillById: (id: number) => Skill | undefined;
  findSkillsByName: (searchTerm: string) => Skill[];
  getAvailableSkillsForLinking: (currentSkillId: number, existingLinks?: number[]) => Skill[];
  getParentSkills: (skillId: number) => Skill[];
  getChildSkills: (skillId: number) => Skill[];
  getSkillTree: (rootSkillId?: number) => {
    skills: Skill[];
    links: TreeLink[];
  };
  refreshSkills: () => Promise<void>;
  // NEW: map cached Skill to frontend SkillHeader objects
  getSkillHeaders: () => SkillHeader[];
   updateSkillInCache: (skill: any) => void;
  removeSkillFromCache: (skillID: number) => void;
  updateSkillLinksInCache: (skillID: number, parentSkills: any[], offspringSkills: any[]) => void;
}

interface SkillsCacheProviderProps {
  token: string;
  children?: React.ReactNode;
  cacheDuration?: number;
}

interface CacheState {
  skills: Skill[];
  links: TreeLink[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

const DEFAULT_CACHE_DURATION = 5 * 60 * 1000;

const SkillsCacheContext = createContext<SkillsCacheContextType | undefined>(undefined);

export const SkillsCacheProvider: React.FC<SkillsCacheProviderProps> = ({ 
  children, 
  token,
  cacheDuration = DEFAULT_CACHE_DURATION 
}) => {
  const [cache, setCache] = useState<CacheState>({
    skills: [],
    links: [],
    loading: false,
    error: null,
    lastUpdated: null,
  });

  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Try to initialize cache from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('skills_cache_v1');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.skills && parsed.links) {
          setCache({
            skills: parsed.skills,
            links: parsed.links,
            loading: false,
            error: null,
            lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : new Date(),
          });
        }
      }
    } catch (err) {
      // parsing failed, ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist cache whenever it updates successfully
  useEffect(() => {
    if (cache.lastUpdated && cache.skills.length > 0) {
      try {
        localStorage.setItem('skills_cache_v1', JSON.stringify({
          skills: cache.skills,
          links: cache.links,
          lastUpdated: cache.lastUpdated.toISOString(),
        }));
      } catch (err) {
        console.error('Failed to persist skills cache to localStorage', err);
      }
    }
  }, [cache.skills, cache.links, cache.lastUpdated]);

const updateSkillInCache = useCallback((skill: any) => {
  setCache(prev => {
    const existingIndex = prev.skills.findIndex(s => s.skillID === skill.skillID);
    let updatedSkills = [...prev.skills];
    
    if (existingIndex >= 0) {
      // Update existing skill
      updatedSkills[existingIndex] = {
        ...updatedSkills[existingIndex],
        skillName: skill.skillName,
        skillDescription: skill.skillDescription,
        hidden: skill.hidden,
      };
    } else {
      // Add new skill (for create mode)
// export interface Skill {
//   skillID: number;
//   skillName: string;
//   criteria1: string;
//   criteria2: string;
//   criteria3: string;
//   criteria4: string;
//   criteria5: string;
//   hidden: number 
//   developmentAge?: string;
//   skillDescription?: string;
//   parentSkills?: SkillHeader[];
//   offspringSkills?: SkillHeader[];
// }
      updatedSkills.push({
        skillID: skill.skillID,
        skillName: skill.skillName,
        criteria1: skill.criteria1 || '',
        criteria2: skill.criteria2 || '',
        criteria3: skill.criteria3 || '',
        criteria4: skill.criteria4 || '',
        criteria5: skill.criteria5 || '',
        hidden: skill.hidden || 0,
        developmentAge: skill.developmentAge || '',
        skillDescription: skill.skillDescription || '',
      });
    }
    
    return { ...prev, skills: updatedSkills };
  });
}, []);

const removeSkillFromCache = useCallback((skillID: number) => {
  setCache(prev => ({
    ...prev,
    skills: prev.skills.filter(s => s.skillID !== skillID),
    links: prev.links.filter(l => l.parentSkillID !== skillID && l.offspringSkillID !== skillID),
  }));
}, []);

const updateSkillLinksInCache = useCallback((skillID: number, parentSkills: any[], offspringSkills: any[]) => {
  setCache(prev => {
    // Remove old links for this skill
    let updatedLinks = prev.links.filter(l => 
      l.parentSkillID !== skillID && l.offspringSkillID !== skillID
    );
    
    // Add new parent links
    parentSkills.forEach(parent => {
      if (!updatedLinks.some(l => l.parentSkillID === parent.skillID && l.offspringSkillID === skillID)) {
        updatedLinks.push({
          parentSkillID: parent.skillID,
          offspringSkillID: skillID,
        });
      }
    });
    
    // Add new offspring links
    offspringSkills.forEach(offspring => {
      if (!updatedLinks.some(l => l.parentSkillID === skillID && l.offspringSkillID === offspring.skillID)) {
        updatedLinks.push({
          parentSkillID: skillID,
          offspringSkillID: offspring.skillID,
        });
      }
    });
    
    return { ...prev, links: updatedLinks };
  });
}, []);


  const isCacheValid = useCallback(() => {
    if (!cache.lastUpdated) return false;
    const expiryTime = new Date(cache.lastUpdated.getTime() + cacheDuration);
    return new Date() < expiryTime;
  }, [cache.lastUpdated, cacheDuration]);

 const fetchSkills = useCallback(async (forceRefresh = false) => {
    if (cache.loading) return;
    if (!forceRefresh && cache.skills.length > 0 && isCacheValid()) return;


    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

   abortControllerRef.current = new AbortController();
    setCache(prev => ({ ...prev, loading: true, error: null }));

    try {
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 30000);

      const data: SkillsTreeResponse = await api.get(API_ENDPOINTS.skills.allWithLinks, {
        timeout: 30000,
        token,
      });

      clearTimeout(timeoutId);

      if (!isMounted.current) return;

      setCache({
        skills: data.skills,
        links: data.links,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (err) {
      if (!isMounted.current) return;
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      // Fallback: try to use localStorage data if available
      try {
        const stored = localStorage.getItem('skills_cache_v1');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.skills && parsed.links) {
            setCache({
              skills: parsed.skills,
              links: parsed.links,
              loading: false,
              error: 'Offline: Loaded cached skills from local storage',
              lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : new Date(),
            });
            return;
          }
        }
      } catch (e) {
        // ignore local cache fallback errors
      }

      const errorMessage = err instanceof Error 
        ? err.message 
        : 'An unknown error occurred while fetching skills tree';
      
      console.error('Error fetching skills tree:', err);
      
      setCache(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    } finally {
      abortControllerRef.current = null;
    }
  }, [token, cache.skills.length, cache.loading, isCacheValid, cacheDuration]);

  const clearCache = useCallback(() => {
    setCache({
      skills: [],
      links: [],
      loading: false,
      error: null,
      lastUpdated: null,
    });
  }, []);

  const getSkillById = useCallback((id: number): Skill | undefined => {
    return cache.skills.find(skill => skill.skillID === id);
  }, [cache.skills]);

  const findSkillsByName = useCallback((searchTerm: string): Skill[] => {
    if (!searchTerm.trim()) return cache.skills;
    
    const term = searchTerm.toLowerCase();
    return cache.skills.filter(skill => 
      skill.skillName.toLowerCase().includes(term)
    );
  }, [cache.skills]);

  const getParentSkills = useCallback((skillId: number): Skill[] => {
    const parentIds = cache.links
      .filter(link => link.offspringSkillID === skillId)
      .map(link => link.parentSkillID);
    
    return parentIds
      .map(id => getSkillById(id))
      .filter((skill): skill is Skill => skill !== undefined);
  }, [cache.links, getSkillById]);

  const getChildSkills = useCallback((skillId: number): Skill[] => {
    const childIds = cache.links
      .filter(link => link.parentSkillID === skillId)
      .map(link => link.offspringSkillID);
    
    return childIds
      .map(id => getSkillById(id))
      .filter((skill): skill is Skill => skill !== undefined);
  }, [cache.links, getSkillById]);

  const getAvailableSkillsForLinking = useCallback((currentSkillId: number, existingLinks: number[] = []): Skill[] => {
    return cache.skills.filter(skill => 
      skill.skillID !== currentSkillId && 
      !existingLinks.includes(skill.skillID)
    );
  }, [cache.skills]);

  const getSkillTree = useCallback((rootSkillId?: number) => {
    if (!rootSkillId) {
      return {
        skills: cache.skills,
        links: cache.links,
      };
    }

    const visited = new Set<number>();
    const skillsInTree: Skill[] = [];
    const linksInTree: TreeLink[] = [];
    
    const traverse = (skillId: number) => {
      if (visited.has(skillId)) return;
      visited.add(skillId);
      
      const skill = getSkillById(skillId);
      if (skill) {
        skillsInTree.push(skill);
      }
      
      const parentLinks = cache.links.filter(link => link.offspringSkillID === skillId);
      parentLinks.forEach(link => {
        if (!linksInTree.some(l => 
          l.parentSkillID === link.parentSkillID && 
          l.offspringSkillID === link.offspringSkillID
        )) {
          linksInTree.push(link);
          traverse(link.parentSkillID);
        }
      });
      
      const childLinks = cache.links.filter(link => link.parentSkillID === skillId);
      childLinks.forEach(link => {
        if (!linksInTree.some(l => 
          l.parentSkillID === link.parentSkillID && 
          l.offspringSkillID === link.offspringSkillID
        )) {
          linksInTree.push(link);
          traverse(link.offspringSkillID);
        }
      });
    };
    
    traverse(rootSkillId);
    
    return {
      skills: skillsInTree,
      links: linksInTree,
    };
  }, [cache.skills, cache.links, getSkillById]);

  const refreshSkills = useCallback(async () => {
    return fetchSkills(true);
  }, [fetchSkills]);

  const getSkillHeaders = useCallback((): SkillHeader[] => {
    return cache.skills.map(s => ({
      skillID: s.skillID,
      skillName: s.skillName,
      skillLinkID: 0, // not present in Skill; default to 0
    }));
  }, [cache.skills]);

  const contextValue: SkillsCacheContextType = {
    skills: cache.skills,
    links: cache.links,
    loading: cache.loading,
    error: cache.error,
    lastUpdated: cache.lastUpdated,
    hasSkills: cache.skills.length > 0,
    fetchSkills, // This matches the interface
    clearCache,
    getSkillById,
    findSkillsByName,
    getAvailableSkillsForLinking,
    getParentSkills,
    getChildSkills,
    getSkillTree,
    refreshSkills,
    getSkillHeaders, 
    updateSkillInCache,
    removeSkillFromCache,
    updateSkillLinksInCache,
  };

  return (
    <SkillsCacheContext.Provider value={contextValue}>
      {children}
    </SkillsCacheContext.Provider>
  );
};

export const useSkillsCache = (): SkillsCacheContextType => {
  const context = useContext(SkillsCacheContext);
  
  if (!context) {
    throw new Error('useSkillsCache must be used within SkillsCacheProvider');
  }
  
  return context;
};

export const useSkillsCacheWithAutoFetch = (autoFetch = true) => {
  const cache = useSkillsCache();
  
  useEffect(() => {
    if (autoFetch && !cache.loading && !cache.hasSkills) {
      cache.fetchSkills();
    }
  }, [autoFetch, cache]);
  
  return cache;
};